
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LanguageSelector from "@/components/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageTranslation, PAGE_PATHS } from "@/hooks/usePageTranslation";
import TranslatedText from "@/components/TranslatedText";
import { useQuery } from "@tanstack/react-query";
import { getPhotos, getPhotoUrl } from "@/services/photoService";
import { useReveal } from "@/hooks/useReveal";
import { Sofa, BedDouble, ShowerHead, Waves, Check } from "lucide-react";

const TABS = [
  { id: "interior", label: "Intérieur", icon: Sofa },
  { id: "bedrooms", label: "Chambres", icon: BedDouble },
  { id: "bathrooms", label: "Salles de bain", icon: ShowerHead },
  { id: "exterior", label: "Extérieur & Piscine", icon: Waves },
];

const FEATURES = {
  interior: {
    heading: "Espace de vie",
    image_category: "intérieur",
    items: [
      "Espace de vie spacieux et lumineux",
      "Cuisine ouverte entièrement équipée",
      "Salle à manger pouvant accueillir 8 personnes",
      "Salon confortable avec smart TV 55 pouces",
      "Connexion Wi-Fi haut débit gratuite",
    ],
  },
  bedrooms: {
    heading: "Chambres confortables",
    image_category: "chambre",
    items: [
      "4 chambres pouvant accueillir jusqu'à 8 personnes",
      "Chambre principale avec lit king-size et salle de bain privative",
      "Seconde chambre avec lit queen-size",
      "Troisième chambre avec lit queen-size",
      "Quatrième chambre avec deux lits simples",
      "Literie de qualité hôtelière",
      "Placards spacieux dans chaque chambre",
      "Climatisation individuelle dans chaque chambre",
    ],
  },
  bathrooms: {
    heading: "Salles de bain modernes",
    image_category: "salle de bain",
    items: [
      "3 salles de bain élégantes",
      "2 salles de bain avec douche",
      "1 salle de bain avec baignoire",
      "Sèche-cheveux dans chaque salle de bain",
      "Serviettes de qualité fournies",
      "Produits d'accueil haut de gamme",
    ],
  },
  exterior: {
    heading: "Extérieur & Piscine",
    image_category: "piscine",
    items: [
      "Piscine privée chauffée (8m × 4m) à ~28°C",
      "Grande terrasse ensoleillée avec mobilier d'extérieur",
      "Espace repas extérieur couvert",
      "Plancha à gaz",
      "Transats confortables autour de la piscine",
      "Douche extérieure",
    ],
  },
};

const Equipment = () => {
  const [activeTab, setActiveTab] = useState("interior");
  const { language } = useLanguage();
  usePageTranslation(PAGE_PATHS.EQUIPMENT);

  const headerReveal = useReveal<HTMLDivElement>();
  const contentReveal = useReveal<HTMLDivElement>();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { data: photos } = useQuery({ queryKey: ["equipment-photos"], queryFn: getPhotos });

  const imageFor = (category: string) => {
    const p = photos?.find((ph) => ph.category?.toLowerCase() === category.toLowerCase());
    return p ? getPhotoUrl(p.storage_path) : null;
  };

  const tab = FEATURES[activeTab as keyof typeof FEATURES];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Helmet>
        <title>Équipements & prestations — Villa Douce Vague</title>
        <meta name="description" content="Équipements de la Villa Douce Vague au Grau du Roi : 4 chambres climatisées, cuisine équipée, piscine chauffée, Wi-Fi, jardin privé et prestations haut de gamme." />
        <meta property="og:title" content="Équipements & prestations — Villa Douce Vague" />
        <meta property="og:description" content="Chambres, cuisine, piscine chauffée et services de la Villa Douce Vague au Grau du Roi." />
        <meta property="og:url" content="https://www.villadoucevague.fr/equipements" />
      </Helmet>
      <Navbar rightElement={<LanguageSelector />} />

      <main className="flex-1">
        {/* Page header */}
        <div className="bg-villa-offwhite pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div
              ref={headerReveal.ref}
              className={`reveal ${headerReveal.revealed ? "in" : ""}`}
            >
              <p className="eyebrow mb-4">La Villa</p>
              <h1 className="heading-lg text-villa-text mb-4">
                <TranslatedText>Équipements & Prestations</TranslatedText>
              </h1>
              <p className="body-lg max-w-2xl">
                <TranslatedText>
                  Découvrez les équipements luxueux de la Villa Douce Vague pour un séjour inoubliable au Grau du Roi.
                </TranslatedText>
              </p>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="sticky top-14 z-30 bg-white border-b border-black/[0.06]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="flex gap-0 overflow-x-auto scrollbar-hide">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 ${
                    activeTab === id
                      ? "border-villa-turquoise text-villa-turquoise"
                      : "border-transparent text-villa-text-secondary hover:text-villa-text"
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                  <TranslatedText>{label}</TranslatedText>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab content */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 md:py-24">
          <div
            ref={contentReveal.ref}
            key={activeTab}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 reveal ${contentReveal.revealed ? "in" : ""}`}
          >
            {/* Features */}
            <div className="flex flex-col justify-center">
              <h2 className="font-playfair text-3xl font-bold text-villa-text mb-8">
                <TranslatedText>{tab.heading}</TranslatedText>
              </h2>
              <ul className="space-y-4">
                {tab.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-villa-turquoise/15 flex items-center justify-center mt-0.5">
                      <Check className="h-3 w-3 text-villa-turquoise" strokeWidth={2.5} />
                    </span>
                    <span className="text-villa-text-secondary leading-relaxed">
                      <TranslatedText>{item}</TranslatedText>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Photo */}
            <div className="relative overflow-hidden rounded-3xl bg-villa-offwhite" style={{ minHeight: 400 }}>
              {imageFor(tab.image_category) ? (
                <img
                  src={imageFor(tab.image_category)!}
                  alt={tab.heading}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-villa-offwhite to-villa-turquoise/10" />
              )}
            </div>
          </div>
        </div>

        {/* Additional service callout */}
        <div className="bg-villa-offwhite py-16">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
            <p className="eyebrow mb-4">Services</p>
            <h2 className="font-playfair text-2xl font-bold text-villa-text mb-4">
              <TranslatedText>Services additionnels sur demande</TranslatedText>
            </h2>
            <p className="text-villa-text-secondary mb-8">
              <TranslatedText>Massages, soins bien-être à domicile et autres services peuvent être organisés selon vos souhaits.</TranslatedText>
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-6 py-3 rounded-full bg-villa-blue text-white text-sm font-medium hover:bg-villa-blue/90 transition-colors"
            >
              <TranslatedText>Nous contacter</TranslatedText>
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Equipment;
